import React, { useState, useCallback, useEffect } from 'react';
import { Box, Chip, IconButton, Tooltip, Menu, MenuItem, Checkbox, ListItemText } from '@mui/material';
import { FilterListOff } from '@mui/icons-material';
import TaskCalendar from '../components/Calendar/TaskCalendar';
import TaskDetailDialog from '../components/Tasks/TaskDetailDialog';
import TaskDialog from '../components/Tasks/TaskDialog';
import { TaskDTO } from '../api';
import NewTaskButton from "../components/Tasks/NewTaskButton";
import NewLeadButton from "../components/Leads/NewLeadButton";
import { useSearchParams } from 'react-router-dom';
import { 
  getStatusColor, 
  getStatusLabel,
  getTaskTypeColor, 
  getTaskTypeLabel, 
  getPriorityColor,
  getPriorityLabel,
  TASK_STATUS_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_TYPE
} from '../models/TaskDTO';


const CalendarPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedTask, setSelectedTask] = useState<TaskDTO | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
// Read URL search parameters
  const [searchParams] = useSearchParams();

  // Parse date and view parameters from URL
  const initialDate = searchParams.get('date') ? new Date(searchParams.get('date')!) : undefined;
  const initialView = searchParams.get('view') as 'month' | 'week' | 'workWeek' | 'day' | 'agenda' | undefined;

  // Filtering state
  const [searchTerm] = useState<string>('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [priorityFilters, setPriorityFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);

  // Menu anchor states for filter dropdowns
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<HTMLElement | null>(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<HTMLElement | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  // New task dialog state
  const [newTaskDialogOpen, setNewTaskDialogOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);

  // Filter menu handlers
  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };

  const handlePriorityMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPriorityMenuAnchor(event.currentTarget);
  };

  const handlePriorityMenuClose = () => {
    setPriorityMenuAnchor(null);
  };

  const handleTypeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTypeMenuAnchor(event.currentTarget);
  };

  const handleTypeMenuClose = () => {
    setTypeMenuAnchor(null);
  };

  // Filter helper functions
  const toggleStatusFilter = (status: string) => {
    setStatusFilters(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const togglePriorityFilter = (priority: string) => {
    setPriorityFilters(prev => 
      prev.includes(priority) 
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  const toggleTypeFilter = (type: string) => {
    setTypeFilters(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const clearAllFilters = () => {
    setStatusFilters([]);
    setPriorityFilters([]);
    setTypeFilters([]);
  };

  // Get filter display text
  const getStatusFilterText = () => {
    if (statusFilters.length === 0) return 'Status';
    if (statusFilters.length === 1) {
      const option = TASK_STATUS_OPTIONS.find(opt => opt.value === statusFilters[0]);
      return option ? option.label : 'Status';
    }
    return `Status (${statusFilters.length})`;
  };

  const getPriorityFilterText = () => {
    if (priorityFilters.length === 0) return 'Priorytet';
    if (priorityFilters.length === 1) {
      const option = TASK_PRIORITY_OPTIONS.find(opt => opt.value === priorityFilters[0]);
      return option ? option.label : 'Priorytet';
    }
    return `Priorytet (${priorityFilters.length})`;
  };

  const getTypeFilterText = () => {
    if (typeFilters.length === 0) return 'Typ zadania';
    if (typeFilters.length === 1) {
      const option = TASK_TYPE.find(opt => opt.value === typeFilters[0]);
      return option ? option.label : 'Typ zadania';
    }
    return `Typ zadania (${typeFilters.length})`;
  };

  // Funkcja odświeżająca kalendarz
  const refreshCalendar = useCallback(async () => {
    setRefreshTrigger(prev => prev + 1);
  }, []);


  // Nasłuchiwanie na globalne zdarzenie odświeżania zadań
  useEffect(() => {
    const handleRefreshTasks = () => {
      refreshCalendar();
    };

    window.addEventListener('refresh-tasks', handleRefreshTasks);

    return () => {
      window.removeEventListener('refresh-tasks', handleRefreshTasks);
    };
  }, [refreshCalendar]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showDetailsPanel, setShowDetailsPanel] = useState<boolean>(false);

  // Obsługa wyboru zadania
  const handleTaskSelect = (task: TaskDTO) => {
    setSelectedTask(task);
    setShowDetailsPanel(true);

    // Jeśli zadanie ma ID, otwórz dialog szczegółów
    if (task.id) {
      setSelectedTaskId(task.id);
      setDetailDialogOpen(true);
    } else {
      // Jeśli zadanie nie ma ID, to jest nowe zadanie - otwórz dialog tworzenia
      const startDate = task.startDate ? new Date(task.startDate) : new Date();
      const endDate = task.endDate ? new Date(task.endDate) : new Date(startDate.getTime() + 60 * 60 * 1000);

      setSelectedDate(startDate);
      setSelectedEndDate(endDate);
      setNewTaskDialogOpen(true);
    }
  };

  // Obsługa kliknięcia na godzinę w widoku dziennym/tygodniowym
  const handleHourClick = (date: Date) => {
    // Ustaw datę początkową na klikniętą godzinę
    const startDate = new Date(date);
    // Ustaw datę końcową na 15 minut później (domyślnie dla większości typów zadań)
    // TaskDialog automatycznie dostosuje czas końcowy na podstawie wybranego typu zadania
    const endDate = new Date(date);
    endDate.setMinutes(endDate.getMinutes() + 15);

    // Ustaw daty dla dialogu
    setSelectedDate(startDate);
    setSelectedEndDate(endDate);
    setNewTaskDialogOpen(true);
  };


  // Obsługa zamykania dialogu szczegółów zadania
  const handleCloseTaskDetail = () => {
    setSelectedTaskId(null);
    setDetailDialogOpen(false);
  };

  // Obsługa zamykania dialogu nowego zadania
  const handleCloseNewTaskDialog = () => {
    setNewTaskDialogOpen(false);
    setSelectedDate(null);
    setSelectedEndDate(null);
  };

  // Obsługa tworzenia nowego zadania
  const handleCreateTask = async (task: TaskDTO) => {
    // TaskDialog handles creation internally, we just need to refresh the calendar
    // to show the newly created task immediately
    setRefreshTrigger(prev => prev + 1);
  };


  // Zamknięcie panelu szczegółów
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTask(null);
  };

  // Odświeżenie kalendarza po zapisie zadania
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTaskSaved = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Funkcje obsługi filtrów
  return (
    <Box>
      {/* Action Buttons and Filter Pills on the same line */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        {/* Left side - Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ minWidth: 120 }}>
            <NewLeadButton />
          </Box>
          <Box sx={{ minWidth: 120 }}>
            <NewTaskButton refreshTasks={refreshCalendar} />
          </Box>
        </Box>

        {/* Right side - Filter Pills */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Status Filter Pill */}
          <Chip
            label={getStatusFilterText()}
            onClick={handleStatusMenuOpen}
            variant={statusFilters.length > 0 ? 'filled' : 'outlined'}
            sx={{
              minWidth: 120,
              backgroundColor: statusFilters.length > 0 ? '#2196f3' : 'transparent',
              color: statusFilters.length > 0 ? 'white' : '#2196f3',
              borderColor: '#2196f3',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: statusFilters.length > 0 ? '#1976d2' : '#e3f2fd',
              }
            }}
          />

          {/* Priority Filter Pill */}
          <Chip
            label={getPriorityFilterText()}
            onClick={handlePriorityMenuOpen}
            variant={priorityFilters.length > 0 ? 'filled' : 'outlined'}
            sx={{
              minWidth: 120,
              backgroundColor: priorityFilters.length > 0 ? '#ff9800' : 'transparent',
              color: priorityFilters.length > 0 ? 'white' : '#ff9800',
              borderColor: '#ff9800',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: priorityFilters.length > 0 ? '#f57c00' : '#fff3e0',
              }
            }}
          />

          {/* Type Filter Pill */}
          <Chip
            label={getTypeFilterText()}
            onClick={handleTypeMenuOpen}
            variant={typeFilters.length > 0 ? 'filled' : 'outlined'}
            sx={{
              minWidth: 120,
              backgroundColor: typeFilters.length > 0 ? '#4caf50' : 'transparent',
              color: typeFilters.length > 0 ? 'white' : '#4caf50',
              borderColor: '#4caf50',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: typeFilters.length > 0 ? '#388e3c' : '#e8f5e8',
              }
            }}
          />

          {/* Clear Filters Button */}
          {(statusFilters.length > 0 || priorityFilters.length > 0 || typeFilters.length > 0) && (
            <Tooltip title="Wyczyść filtry">
              <IconButton
                size="small"
                onClick={clearAllFilters}
              >
                <FilterListOff fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Calendar */}
      <Box sx={{ mt: 2, width: '100%' }}>
        <TaskCalendar 
          onTaskSelect={handleTaskSelect}
          onHourClick={handleHourClick}
          refreshTrigger={refreshTrigger}
          searchTerm={searchTerm}
          statusFilters={statusFilters}
          priorityFilters={priorityFilters}
          typeFilters={typeFilters}
          initialDate={initialDate}
          initialView={initialView}
        />
      </Box>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        open={detailDialogOpen}
        taskId={selectedTaskId}
        onClose={handleCloseTaskDetail}
        onTaskUpdated={() => {
          setRefreshTrigger(prev => prev + 1);
        }}
      />

      {/* New Task Dialog */}
      <TaskDialog
        open={newTaskDialogOpen}
        onClose={handleCloseNewTaskDialog}
        task={null}
        onSave={handleCreateTask}
        refreshTasks={refreshCalendar}
        initialStartDate={selectedDate || undefined}
        initialEndDate={selectedEndDate || undefined}
      />

      {/* Status Filter Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {TASK_STATUS_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => toggleStatusFilter(option.value)}
            dense
          >
            <Checkbox
              checked={statusFilters.includes(option.value)}
              size="small"
            />
            <ListItemText
              primary={option.label}
              sx={{
                color: getStatusColor(option.value),
                fontWeight: statusFilters.includes(option.value) ? 'bold' : 'normal',
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Priority Filter Menu */}
      <Menu
        anchorEl={priorityMenuAnchor}
        open={Boolean(priorityMenuAnchor)}
        onClose={handlePriorityMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {TASK_PRIORITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => togglePriorityFilter(option.value)}
            dense
          >
            <Checkbox
              checked={priorityFilters.includes(option.value)}
              size="small"
            />
            <ListItemText
              primary={option.label}
              sx={{
                color: getPriorityColor(option.value),
                fontWeight: priorityFilters.includes(option.value) ? 'bold' : 'normal',
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Type Filter Menu */}
      <Menu
        anchorEl={typeMenuAnchor}
        open={Boolean(typeMenuAnchor)}
        onClose={handleTypeMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {TASK_TYPE.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => toggleTypeFilter(option.value)}
            dense
          >
            <Checkbox
              checked={typeFilters.includes(option.value)}
              size="small"
            />
            <ListItemText
              primary={option.label}
              sx={{
                color: getTaskTypeColor(option.value),
                fontWeight: typeFilters.includes(option.value) ? 'bold' : 'normal',
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default CalendarPage;
